import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import {
  computePlacement,
  type Placement,
  type Rect,
} from "@/lib/edge-aware-placement";
import { measureUntransformed } from "@/lib/measure-untransformed";

export const BUBBLE_INSET = 8;

export function clampFrozenBubblePosition(
  position: { left: number; top: number },
  size: { width: number; height: number },
  viewport: Rect,
) {
  const minLeft = viewport.left + BUBBLE_INSET;
  const minTop = viewport.top + BUBBLE_INSET;
  return {
    left: Math.min(
      Math.max(position.left, minLeft),
      Math.max(minLeft, viewport.right - BUBBLE_INSET - size.width),
    ),
    top: Math.min(
      Math.max(position.top, minTop),
      Math.max(minTop, viewport.bottom - BUBBLE_INSET - size.height),
    ),
  };
}

export function computeEdgeAwareBubblePlacement({
  anchor,
  size,
  viewport,
  exclusions = [],
  reply = false,
}: {
  anchor: { x: number; y: number };
  size: { width: number; height: number };
  viewport: Rect;
  exclusions?: Rect[];
  reply?: boolean;
}) {
  const result = computePlacement({
    anchor,
    size,
    viewport,
    inset: BUBBLE_INSET,
    anchorOffset: 0,
    exclusions,
  });
  return {
    ...result,
    top: result.top + (reply && result.placement.startsWith("bottom") ? 8 : 0),
  };
}

/** Positions a desktop status surface and reports its visible window rect. */
export function useEdgeAwareBubblePlacement({
  exclusions,
  reply,
  frozen,
  onRect,
  containerRef,
  manualPosition,
  positionResetKey,
}: {
  exclusions?: Rect[];
  reply: boolean;
  frozen: boolean;
  onRect?: (rect: Rect) => void;
  containerRef?: RefObject<HTMLDivElement | null>;
  manualPosition?: RefObject<{ left: number; top: number } | null>;
  positionResetKey?: string;
}) {
  const ownRef = useRef<HTMLDivElement>(null);
  const ref = containerRef ?? ownRef;
  const placementRef = useRef<Placement>("bottom-right");
  const positionRef = useRef<{ left: number; top: number } | null>(null);
  const [placement, setPlacement] = useState<Placement>("bottom-right");
  useLayoutEffect(() => {
    positionRef.current = null;
  }, [positionResetKey]);
  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;
  const onRectRef = useRef(onRect);
  onRectRef.current = onRect;
  const exclusionsRef = useRef(exclusions);
  exclusionsRef.current = exclusions;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    let frame = 0;
    const apply = () => {
      const box = measureUntransformed(element);
      const pane =
        (element.closest("[data-canvas-viewport]") as HTMLElement | null) ??
        document.documentElement;
      const viewport = pane.getBoundingClientRect();
      const callouts = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-agent-annotation-callout="true"]',
        ),
        (callout) => callout.getBoundingClientRect(),
      );
      const heldPosition = manualPosition?.current ?? positionRef.current;
      if ((!manualPosition?.current && !frozenRef.current) || !heldPosition) {
        const result = computeEdgeAwareBubblePlacement({
          anchor: { x: box.left, y: box.top },
          size: { width: box.width, height: box.height },
          viewport,
          exclusions: [...(exclusionsRef.current ?? []), ...callouts],
          reply,
        });
        positionRef.current = { left: result.left, top: result.top };
        element.style.transform = `translate(${result.left - box.left}px, ${result.top - box.top}px)`;
        if (placementRef.current !== result.placement) {
          placementRef.current = result.placement;
          element.dataset.placement = result.placement;
          setPlacement(result.placement);
        }
      } else {
        const position = clampFrozenBubblePosition(
          heldPosition,
          { width: box.width, height: box.height },
          viewport,
        );
        positionRef.current = position;
        if (manualPosition?.current) manualPosition.current = position;
        element.style.transform = `translate(${position.left - box.left}px, ${position.top - box.top}px)`;
      }
      // The translate only offsets the measured box, so the visible rect is
      // known without forcing a second layout.
      const { left, top } = positionRef.current;
      onRectRef.current?.({
        left,
        top,
        right: left + box.width,
        bottom: top + box.height,
      });
      frame = requestAnimationFrame(apply);
    };
    apply();
    return () => cancelAnimationFrame(frame);
  }, [reply, ref, manualPosition]);

  return { ref, placement };
}
